import { routeFileSelector } from '@/utilz';
import Button from '@/views/components/Buttons/Button';
import FormCheckBox from '@/views/components/Inputs/FormCheckBox';
import BatchLabel from '@/views/components/Labels/BatchLabel';
import PrimaryLabel from '@/views/components/Labels/PrimaryLabel';
import SecondaryLabel from '@/views/components/Labels/SecondaryLabel';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import ContentSpinner from '@/views/components/Spinners/ContentSpinner';
import { Dropdown, Popconfirm } from 'antd';
import moment from 'moment';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ReactComponent as MoreIcon } from '../../../../assets/icons/dots-v.svg';
import { SaversShipmentColumns } from './constants';

const CHECKBOX_COLUMN_WIDTH = 32;

const dateFormatter = (cell) => {
  const newDate = cell && moment(cell).format('MMM D YYYY');
  return <span>{newDate}</span>;
};

const SaversShipmentGridView = ({
  handleLoadMore,
  isLoading,
  hasMore,
  shipmentList,
  handleShipmentClick,
  onClickStatusUpdate,
  handleDelete,
  onSelect,
  selectedShipments,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const tableViewColumns = SaversShipmentColumns;
  const getMenu = (data) => [
    {
      key: '2',
      label: (
        <Popconfirm
          placement="topRight"
          title={t('translation:default.fields.deleteConfirm.title')}
          onConfirm={() => handleDelete(data.shipmentKey)}
          okText={t('translation:default.options.yes')}
          cancelText={t('translation:default.buttons.cancel')}
          overlayClassName=""
        >
          <div>{t('translation:default.buttons.delete')}</div>
        </Popconfirm>
      ),
    },
    {
      key: '5',
      label: (
        <div
          className="single-line"
          onClick={() => onClickStatusUpdate(data)}
          onKeyDown={() => onClickStatusUpdate(data)}
        >
          Update Status
        </div>
      ),
    },
  ];

  const handleCheckBoxClick = (e, data, isSelected) => {
    e.stopPropagation();
    onSelect(data.shipmentKey, isSelected);
  };

  const colSpan = useMemo(
    () => (tableViewColumns?.data?.length ? tableViewColumns?.data?.length : 0) + 3,
    [tableViewColumns?.data?.length],
  );

  return (
    <div className="h-100 overflow-hidden pb-15">
      {tableViewColumns.progressing ? (
        <div className="h-100 background-white br-4">
          <ContentSpinner />
        </div>
      ) : (
        <>
          <ScrollPagination
            className="background-white br-4 w-100 h-100 scrollable-table-container horizontal"
            loadMore={handleLoadMore}
            hasMore={hasMore}
            isLoading={isLoading}
            scrollOffset={50}
          >
            <table className="w-100 background-white br-4">
              <thead>
                <tr>
                  <th
                    className="background-header text-white fixed fixed-top fixed-left shadow-none"
                    style={{
                      width: CHECKBOX_COLUMN_WIDTH,
                      maxWidth: CHECKBOX_COLUMN_WIDTH,
                      minWidth: CHECKBOX_COLUMN_WIDTH,
                    }}
                  />
                  <th
                    className="background-header text-white fixed fixed-top fixed-left"
                    style={{
                      left: CHECKBOX_COLUMN_WIDTH,
                      whiteSpace: 'pre-wrap !important',
                    }}
                  >
                    Shipment No
                  </th>
                  {(tableViewColumns?.data ? tableViewColumns.data : []).map(({ columnName, columnValue }) => (
                    <th key={columnValue} className="background-header text-white fixed fixed-top">
                      {columnName}
                    </th>
                  ))}
                  <th className="background-header text-white fixed fixed-top fixed-right" />
                </tr>
              </thead>
              <tbody>
                {shipmentList.map((row) => (
                  <tr key={row.shipmentKey}>
                    <td
                      className="fixed fixed-left shadow-none"
                      style={{
                        width: CHECKBOX_COLUMN_WIDTH,
                        maxWidth: CHECKBOX_COLUMN_WIDTH,
                        minWidth: CHECKBOX_COLUMN_WIDTH,
                      }}
                    >
                      <FormCheckBox
                        className="m-0"
                        checkBoxClass="normal-checkbox"
                        checked={selectedShipments.includes(row.shipmentKey)}
                        onChange={(e) => handleCheckBoxClick(e, row, selectedShipments.includes(row.shipmentKey))}
                      />
                    </td>
                    <td className="fixed fixed-left" style={{ left: CHECKBOX_COLUMN_WIDTH }}>
                      <span
                        className="cursor-pointer f-18 fw-700 text-header "
                        onClick={() => handleShipmentClick(row.shipmentKey)}
                        onKeyDown={() => handleShipmentClick(row.shipmentKey)}
                      >
                        {row.shipmentNo}
                      </span>
                    </td>
                    {(tableViewColumns?.data ? tableViewColumns.data : []).map(({ columnValue }) => (
                      <GenerateColumnValues key={columnValue} fieldName={columnValue} value={row[columnValue]} />
                    ))}
                    <td className="pl-5 pr-8 fixed fixed-right text-center">
                      <Dropdown menu={{ items: getMenu(row) }} placement="bottomLeft">
                        <Button
                          ghost
                          className="px-5 min-width-auto bg-none border-none p-0 shadow-none"
                          size="small"
                          shape="circle"
                          type="ghost"
                        >
                          <MoreIcon />
                        </Button>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
                {!isLoading && shipmentList?.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="background-white text-center">
                      <PrimaryLabel className="text-danger">
                        {t('translation:default.notification.emptyList')}.{'  '}
                        {t('translation:default.emptyRecords.title')}.
                      </PrimaryLabel>
                    </td>
                  </tr>
                ) : (
                  <></>
                )}
              </tbody>
            </table>
          </ScrollPagination>
        </>
      )}
    </div>
  );
};

const GenerateColumnValues = memo(({ fieldName, value }) => {
  const generateFieldValues = (fieldName, value) => {
    switch (fieldName) {
      case 'status':
        return (
          <td key={fieldName}>
            <BatchLabel className="background-statusColor f-15">{value}</BatchLabel>
          </td>
        );
      case 'eta':
        return <td key={fieldName}>{dateFormatter(value)}</td>;
      case 'etd':
        return <td key={fieldName}>{dateFormatter(value)}</td>;
      default:
        return (
          <td key={fieldName}>
            <SecondaryLabel className="f-15 fw-600 text-black">{value}</SecondaryLabel>
          </td>
        );
    }
  };
  return <>{generateFieldValues(fieldName, value)}</>;
});

export default SaversShipmentGridView;
